/**
 * Training LMS — catálogo + atribuições + quizzes.
 * Route: /training
 *
 * Vista adaptativa:
 * - Admin: gestão de cursos + atribuições + relatório literacia
 * - Todos: as minhas formações + fazer quizzes
 */
import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { GraduationCap, BookOpen, Award, Clock, CheckCircle2, XCircle, AlertTriangle, Loader2, PlayCircle, Send, Users, Fish, Trash2, Plus, Pencil, Settings2, Filter, Download, Sparkles, BadgeCheck, LayoutDashboard, Share2, Eye, Repeat, Globe, Link2, Upload, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const CATEGORY_META = {
  ai_literacy: { label: 'Literacia IA', tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  cybersecurity: { label: 'Cibersegurança', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  phishing: { label: 'Phishing', tone: 'bg-red-50 text-red-700 border-red-200' },
  gdpr: { label: 'GDPR', tone: 'bg-purple-50 text-purple-700 border-purple-200' },
  incident_response: { label: 'Resposta Incidentes', tone: 'bg-orange-50 text-orange-700 border-orange-200' },
  governance: { label: 'Governança', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const FRAMEWORK_META = {
  nis2: { label: 'NIS2', tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  ai_act: { label: 'AI Act', tone: 'bg-violet-50 text-violet-700 border-violet-200' },
  both: { label: 'NIS2 + AI Act', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const EMPTY_COURSE_FORM = {
  title: '',
  description: '',
  category: 'cybersecurity',
  framework: 'nis2',
  duration_min: 20,
  mandatory: false,
  target_roles: 'colaborador',
  body_md: '',
  pass_threshold: 70,
  quiz: [],
  recurrence_months: 0,
};

const STATUS_META = {
  assigned: { label: 'Atribuído', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  in_progress: { label: 'Em progresso', tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  completed: { label: 'Concluído', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed: { label: 'Reprovado', tone: 'bg-red-50 text-red-700 border-red-200' },
};

export default function Training() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isBoardView = ['admin', 'ciso', 'board_member', 'dpo'].includes(user?.role);
  const [tab, setTab] = useState('mine');
  const [courses, setCourses] = useState([]);
  const [mine, setMine] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [stats, setStats] = useState(null);
  const [literacyRows, setLiteracyRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [boardStats, setBoardStats] = useState(null);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [recommendations, setRecommendations] = useState(null);
  const [assigningRec, setAssigningRec] = useState(null); // course_id being assigned
  const [quizPreview, setQuizPreview] = useState(false);
  const [previewAnswers, setPreviewAnswers] = useState([]);
  const [sharingId, setSharingId] = useState(null); // assignment_id being toggled
  const [csvDialog, setCsvDialog] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  // Assignment dialog
  const [assignCourse, setAssignCourse] = useState(null);
  const [assignEmails, setAssignEmails] = useState('');
  const [assignDue, setAssignDue] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Course view + quiz
  const [viewing, setViewing] = useState(null); // {assignment, course}
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Phishing evidences
  const [phishing, setPhishing] = useState([]);
  const [phishStats, setPhishStats] = useState(null);
  const [phishDialog, setPhishDialog] = useState(false);
  const [phishForm, setPhishForm] = useState({
    provider: 'caniphish', name: '', date: new Date().toISOString().slice(0, 10),
    target_count: 100, clicked_count: '', reported_count: '', template_type: '',
    report_url: '', notes: '', remedial_training_assigned: false,
  });

  // Course management (admin)
  const [frameworkFilter, setFrameworkFilter] = useState('all'); // all|nis2|ai_act|both
  const [courseDialog, setCourseDialog] = useState(false); // false | {mode:'create'|'edit', data}
  const [courseForm, setCourseForm] = useState(EMPTY_COURSE_FORM);
  const [savingCourse, setSavingCourse] = useState(false);

  useEffect(() => { document.title = 'Formação · NIS2AI'; }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const requests = [
          api.get('/training/courses'),
          api.get('/training/assignments/mine'),
          api.get('/training/stats'),
          api.get('/training/recommendations'),
        ];
        if (isAdmin) {
          requests.push(api.get('/training/assignments'));
          requests.push(api.get('/training/literacy-report'));
          requests.push(api.get('/training/phishing-campaigns'));
          requests.push(api.get('/training/phishing-campaigns/stats'));
        }
        if (isBoardView) {
          requests.push(api.get('/training/board-dashboard'));
        }
        const results = await Promise.all(requests);
        if (ignore) return;
        setCourses(results[0].data.items || []);
        setMine(results[1].data.items || []);
        setStats(results[2].data);
        setRecommendations(results[3].data);
        let idx = 4;
        if (isAdmin) {
          setAllAssignments(results[idx++].data.items || []);
          setLiteracyRows(results[idx++].data.rows || []);
          setPhishing(results[idx++].data.items || []);
          setPhishStats(results[idx++].data);
        }
        if (isBoardView) {
          setBoardStats(results[idx++].data);
        }
      } catch (err) {
        if (!ignore) toast.error(err?.response?.data?.detail || 'Falha ao carregar formação.');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [refresh, isAdmin, isBoardView]);

  const openCourse = async (assignment) => {
    const course = courses.find(c => c.course_id === assignment.course_id);
    if (!course) {
      toast.error('Curso não disponível.');
      return;
    }
    setViewing({ assignment, course });
    setAnswers(new Array((course.quiz || []).length).fill(-1));
    setResult(null);
  };

  const submitQuiz = async () => {
    if (!viewing) return;
    if (answers.some(a => a === -1)) {
      toast.error('Responda a todas as perguntas antes de submeter.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/training/assignments/${viewing.assignment.assignment_id}/complete`, { answers });
      setResult(data);
      if (data.passed) toast.success(`Aprovado — ${data.score}%`);
      else toast.warning(`Reprovado — ${data.score}% (mínimo ${data.threshold}%). Tente rever o material.`);
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao submeter.');
    } finally {
      setSubmitting(false);
    }
  };

  const openAssign = (course) => {
    setAssignCourse(course);
    setAssignEmails('');
    setAssignDue('');
  };

  const submitAssign = async () => {
    const emails = assignEmails.split(/[,\n\s]+/).map(s => s.trim()).filter(Boolean);
    if (emails.length === 0) {
      toast.error('Introduza pelo menos um email.');
      return;
    }
    setAssigning(true);
    try {
      const { data } = await api.post('/training/assignments', {
        course_id: assignCourse.course_id,
        user_emails: emails,
        due_date: assignDue || null,
      });
      toast.success(`${data.created} atribuições criadas de ${data.requested} solicitadas.`);
      setAssignCourse(null);
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao atribuir.');
    } finally {
      setAssigning(false);
    }
  };

  const submitPhishing = async () => {
    if (!phishForm.name || !phishForm.date || !phishForm.target_count) {
      toast.error('Nome, data e nº de alvos são obrigatórios.');
      return;
    }
    try {
      const payload = {
        ...phishForm,
        target_count: Number(phishForm.target_count),
        clicked_count: phishForm.clicked_count === '' ? null : Number(phishForm.clicked_count),
        reported_count: phishForm.reported_count === '' ? null : Number(phishForm.reported_count),
      };
      await api.post('/training/phishing-campaigns', payload);
      toast.success('Campanha registada como evidência.');
      setPhishDialog(false);
      setPhishForm({ ...phishForm, name: '', clicked_count: '', reported_count: '', notes: '' });
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao registar.');
    }
  };

  const deletePhishing = async (id) => {
    if (!window.confirm('Remover esta campanha?')) return;
    try {
      await api.delete(`/training/phishing-campaigns/${id}`);
      toast.success('Removida.');
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha.');
    }
  };

  // ------------------ Course management (admin) ------------------
  const filteredCourses = useMemo(() => {
    if (frameworkFilter === 'all') return courses;
    if (frameworkFilter === 'both') return courses.filter(c => c.framework === 'both');
    return courses.filter(c => c.framework === frameworkFilter || c.framework === 'both');
  }, [courses, frameworkFilter]);

  const coursesByFramework = useMemo(() => {
    const counts = { nis2: 0, ai_act: 0, both: 0, unknown: 0 };
    courses.forEach(c => {
      const fw = c.framework || 'unknown';
      counts[fw] = (counts[fw] ?? 0) + 1;
    });
    return counts;
  }, [courses]);

  const openCreateCourse = () => {
    setCourseForm({ ...EMPTY_COURSE_FORM });
    setQuizPreview(false);
    setCourseDialog({ mode: 'create' });
  };

  const openEditCourse = (course) => {
    setCourseForm({
      title: course.title || '',
      description: course.description || '',
      category: course.category || 'cybersecurity',
      framework: course.framework || 'both',
      duration_min: course.duration_min || 20,
      mandatory: !!course.mandatory,
      target_roles: (course.target_roles || []).join(', '),
      body_md: course.body_md || '',
      pass_threshold: course.pass_threshold ?? 70,
      quiz: course.quiz || [],
      recurrence_months: course.recurrence_months ?? 0,
    });
    setQuizPreview(false);
    setCourseDialog({ mode: 'edit', data: course });
  };

  const updateQuizQ = (idx, patch) => {
    setCourseForm(f => {
      const next = [...(f.quiz || [])];
      next[idx] = { ...next[idx], ...patch };
      return { ...f, quiz: next };
    });
  };
  const updateQuizOption = (qIdx, optIdx, value) => {
    setCourseForm(f => {
      const next = [...(f.quiz || [])];
      const options = [...(next[qIdx].options || [])];
      options[optIdx] = value;
      next[qIdx] = { ...next[qIdx], options };
      return { ...f, quiz: next };
    });
  };
  const addQuizQ = () => {
    setCourseForm(f => ({
      ...f,
      quiz: [...(f.quiz || []), { q: '', options: ['', ''], correct_index: 0 }],
    }));
  };
  const addQuizOption = (qIdx) => {
    setCourseForm(f => {
      const next = [...(f.quiz || [])];
      const options = [...(next[qIdx].options || []), ''];
      next[qIdx] = { ...next[qIdx], options };
      return { ...f, quiz: next };
    });
  };
  const removeQuizQ = (idx) => {
    setCourseForm(f => ({ ...f, quiz: (f.quiz || []).filter((_, i) => i !== idx) }));
  };

  const submitCourse = async () => {
    // Basic client-side validation
    if (!courseForm.title || courseForm.title.length < 5) return toast.error('Título mínimo 5 caracteres.');
    if (!courseForm.description || courseForm.description.length < 10) return toast.error('Descrição mínimo 10 caracteres.');
    if (!courseForm.body_md || courseForm.body_md.length < 50) return toast.error('Conteúdo mínimo 50 caracteres.');
    for (const q of (courseForm.quiz || [])) {
      if (!q.q || (q.options || []).some(o => !o)) {
        return toast.error('Todas as perguntas do quiz devem ter enunciado e opções preenchidas.');
      }
      if (q.correct_index == null || q.correct_index < 0 || q.correct_index >= (q.options || []).length) {
        return toast.error('Índice da resposta correta inválido em alguma pergunta.');
      }
    }
    const payload = {
      title: courseForm.title.trim(),
      description: courseForm.description.trim(),
      category: courseForm.category,
      framework: courseForm.framework,
      duration_min: Number(courseForm.duration_min) || 20,
      mandatory: !!courseForm.mandatory,
      target_roles: (courseForm.target_roles || '').split(/[,\n]+/).map(s => s.trim()).filter(Boolean),
      body_md: courseForm.body_md,
      pass_threshold: Number(courseForm.pass_threshold) || 70,
      recurrence_months: Number(courseForm.recurrence_months) || 0,
      quiz: (courseForm.quiz || []).map(q => ({
        q: q.q,
        options: q.options,
        correct_index: Number(q.correct_index) || 0,
      })),
    };
    setSavingCourse(true);
    try {
      if (courseDialog.mode === 'edit') {
        await api.put(`/training/courses/${courseDialog.data.course_id}`, payload);
        toast.success('Curso atualizado.');
      } else {
        await api.post('/training/courses', payload);
        toast.success('Curso criado.');
      }
      setCourseDialog(false);
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao guardar curso.');
    } finally {
      setSavingCourse(false);
    }
  };

  const deleteCourse = async (course) => {
    if (!window.confirm(`Eliminar curso "${course.title}"?\nAtribuições concluídas serão mantidas para auditoria.`)) return;
    try {
      await api.delete(`/training/courses/${course.course_id}`);
      toast.success('Curso eliminado.');
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao eliminar.');
    }
  };

  const downloadCertificate = async (assignmentId) => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${backend}/api/training/assignments/${assignmentId}/certificate.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificado_${assignmentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Certificado descarregado (assinado SHA-256).');
    } catch (err) {
      toast.error(err.message || 'Falha ao descarregar certificado.');
    }
  };

  const triggerAutoAssign = async () => {
    if (!window.confirm('Executar auto-atribuição de cursos obrigatórios para TODOS os utilizadores ativos?\n(Operação idempotente — não duplica atribuições existentes.)')) return;
    setAutoAssigning(true);
    try {
      const { data } = await api.post('/training/assignments/auto-assign');
      toast.success(`${data.assignments_created} novas atribuições criadas (${data.users_scanned} utilizadores).`);
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao executar auto-atribuição.');
    } finally {
      setAutoAssigning(false);
    }
  };

  const selfAssignRecommendation = async (courseId) => {
    setAssigningRec(courseId);
    try {
      const { data } = await api.post(`/training/assignments/self/${courseId}`);
      if (data.created > 0) {
        toast.success('Curso adicionado à sua lista.');
      } else {
        toast.info(data.reason || 'Já existe uma atribuição ativa deste curso.');
      }
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao adicionar curso.');
    } finally {
      setAssigningRec(null);
    }
  };

  const toggleShareCertificate = async (assignment) => {
    const next = !assignment.public_visible;
    setSharingId(assignment.assignment_id);
    try {
      await api.post(`/training/assignments/${assignment.assignment_id}/share?public_visible=${next}`);
      toast.success(next ? 'Certificado publicado no seu perfil público.' : 'Certificado removido do perfil público.');
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao atualizar partilha.');
    } finally {
      setSharingId(null);
    }
  };

  const copyPortfolioLink = () => {
    if (!user?.user_id) return;
    const url = `${window.location.origin}/perfil/${user.user_id}`;
    navigator.clipboard.writeText(url);
    toast.success('Link do portefólio copiado.');
  };

  const downloadCsvTemplate = async () => {
    try {
      const { data } = await api.get('/training/courses/csv-template');
      const blob = new Blob([data.template_csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_cursos_nis2ai.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success('Template descarregado.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao obter template.');
    }
  };

  const submitCsvImport = async () => {
    if (!csvContent || csvContent.trim().length < 20) {
      toast.error('CSV vazio ou demasiado curto.');
      return;
    }
    setCsvImporting(true);
    setCsvResult(null);
    try {
      const { data } = await api.post('/training/courses/import-csv', { csv_content: csvContent });
      setCsvResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} curso(s) criado(s).`);
      } else if (data.skipped_duplicate > 0) {
        toast.info(`Todos os cursos já existiam (${data.skipped_duplicate} duplicados).`);
      } else {
        toast.warning('Nenhum curso importado — verifique os erros.');
      }
      setRefresh(r => r + 1);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao importar CSV.');
    } finally {
      setCsvImporting(false);
    }
  };

  const openCsvDialog = () => {
    setCsvContent('');
    setCsvResult(null);
    setCsvDialog(true);
  };

  return (
    <Layout title="Formação & Literacia IA">
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="training-page">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-indigo-600" /> Formação & Literacia IA
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Cobre o <strong>Art. 4 EU AI Act</strong> (literacia em IA, em vigor desde 2/ago/2025) e o <strong>Art. 21(2)(g) NIS2</strong> (formação em cibersegurança da força de trabalho).
          </p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { key: 'courses', label: 'Cursos', value: stats?.total_courses ?? 0, icon: BookOpen, tone: 'from-indigo-500 to-indigo-600' },
            { key: 'assignments', label: 'Atribuições', value: stats?.total_assignments ?? 0, icon: Users, tone: 'from-sky-500 to-sky-600' },
            { key: 'completed', label: 'Concluídas', value: stats?.completed ?? 0, icon: CheckCircle2, tone: 'from-emerald-500 to-emerald-600' },
            { key: 'in_progress', label: 'Em curso', value: stats?.in_progress ?? 0, icon: Clock, tone: 'from-amber-500 to-amber-600' },
            { key: 'overdue', label: 'Em atraso', value: stats?.overdue ?? 0, icon: AlertTriangle, tone: 'from-red-500 to-red-600' },
          ].map(k => (
            <Card key={k.key} data-testid={`training-kpi-${k.key}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{k.label}</span>
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${k.tone} flex items-center justify-center`}>
                    <k.icon className="h-4 w-4 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{k.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="mine" data-testid="tab-mine">As minhas formações ({mine.length})</TabsTrigger>
            <TabsTrigger value="catalog" data-testid="tab-catalog">Catálogo ({courses.length})</TabsTrigger>
            {isBoardView && <TabsTrigger value="board" data-testid="tab-board">Painel Executivo</TabsTrigger>}
            {isAdmin && <TabsTrigger value="manage" data-testid="tab-manage">Gestão de cursos</TabsTrigger>}
            {isAdmin && <TabsTrigger value="assignments" data-testid="tab-assignments">Todas as atribuições</TabsTrigger>}
            {isAdmin && <TabsTrigger value="literacy" data-testid="tab-literacy">Relatório de literacia</TabsTrigger>}
            {isAdmin && <TabsTrigger value="phishing" data-testid="tab-phishing">Phishing (evidências)</TabsTrigger>}
          </TabsList>

          <TabsContent value="mine">
            {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
              <div className="space-y-4">
                {/* Learning Path recommendations */}
                {recommendations?.items?.length > 0 && (
                  <Card data-testid="recommendations-card" className="border-indigo-200 bg-gradient-to-br from-indigo-50/60 via-white to-white">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-indigo-600" /> Trilho de aprendizagem — próximos cursos sugeridos
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {recommendations.items.map((c) => {
                          const fw = FRAMEWORK_META[c.framework] || FRAMEWORK_META.both;
                          const reasons = (recommendations.reasons.find(r => r.course_id === c.course_id) || {}).reasons || [];
                          return (
                            <div key={c.course_id} className="border border-slate-200 rounded-lg p-3 bg-white flex flex-col" data-testid={`recommendation-${c.course_id}`}>
                              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                                <Badge variant="secondary" className={`text-[10px] ${fw.tone}`}>{fw.label}</Badge>
                                {c.mandatory && <Badge variant="secondary" className="text-[10px] bg-red-50 text-red-700 border-red-200">Obrigatório</Badge>}
                              </div>
                              <div className="text-sm font-semibold text-slate-900 line-clamp-2">{c.title}</div>
                              <div className="text-xs text-slate-500 mt-1 mb-2 line-clamp-2">{c.description}</div>
                              {reasons.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {reasons.map((r, i) => (
                                    <span key={i} className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">
                                      {r}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className="mt-auto pt-2 flex items-center justify-between text-[11px] text-slate-500">
                                <span>⏱ {c.duration_min} min</span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => selfAssignRecommendation(c.course_id)}
                                  disabled={assigningRec === c.course_id}
                                  data-testid={`recommend-add-${c.course_id}`}
                                  className="h-7"
                                >
                                  {assigningRec === c.course_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Plus className="h-3 w-3 mr-1" />Adicionar</>}
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Portfolio public link */}
                {mine.some(a => a.status === 'completed' && a.passed) && (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-center gap-3 text-xs" data-testid="portfolio-link-banner">
                    <div className="w-8 h-8 rounded-md bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Globe className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-800">Portefólio público</div>
                      <div className="text-slate-500">Publique certificados no seu perfil e partilhe o link com auditores ou recrutadores.</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={copyPortfolioLink} data-testid="portfolio-copy-link">
                      <Link2 className="h-3.5 w-3.5 mr-1" /> Copiar link
                    </Button>
                    <a
                      href={`/perfil/${user?.user_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-slate-200 hover:bg-white text-slate-700 text-xs"
                      data-testid="portfolio-open"
                    >
                      <Eye className="h-3.5 w-3.5" /> Ver
                    </a>
                  </div>
                )}

                {mine.length === 0 ? (
                  <Card><CardContent className="p-8 text-center text-slate-400 text-sm">Ainda não tem formações atribuídas. Contacte o administrador.</CardContent></Card>
                ) : (
                  <div className="space-y-2">
                    {mine.map(a => {
                      const status = STATUS_META[a.status] || STATUS_META.assigned;
                      return (
                        <Card key={a.assignment_id} className="hover:border-indigo-200 cursor-pointer transition-colors" onClick={() => openCourse(a)} data-testid={`mine-${a.assignment_id}`}>
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                              <BookOpen className="h-5 w-5 text-indigo-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="secondary" className={status.tone}>{status.label}</Badge>
                                {a.due_date && <span className="text-xs text-slate-500">📅 até {a.due_date}</span>}
                                {a.quiz_score !== null && a.quiz_score !== undefined && (
                                  <span className={`text-xs font-bold ${a.passed ? 'text-emerald-600' : 'text-red-600'}`}>
                                    {a.passed ? '✓' : '✗'} {a.quiz_score}%
                                  </span>
                                )}
                              </div>
                              <h3 className="font-semibold text-slate-900">{a.course_title}</h3>
                              <p className="text-xs text-slate-500">Atribuído em {(a.assigned_at || '').slice(0, 10)}</p>
                            </div>
                            {a.status !== 'completed' && (
                              <Button size="sm" variant="outline">
                                <PlayCircle className="h-3.5 w-3.5 mr-1" /> Iniciar
                              </Button>
                            )}
                            {a.status === 'completed' && a.passed && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => { e.stopPropagation(); downloadCertificate(a.assignment_id); }}
                                  data-testid={`cert-download-${a.assignment_id}`}
                                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                                  title="Descarregar certificado assinado (SHA-256)"
                                >
                                  <BadgeCheck className="h-3.5 w-3.5 mr-1" /> Certificado
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => { e.stopPropagation(); toggleShareCertificate(a); }}
                                  disabled={sharingId === a.assignment_id}
                                  data-testid={`cert-share-${a.assignment_id}`}
                                  title={a.public_visible ? 'Remover do perfil público' : 'Publicar no perfil público'}
                                  className={a.public_visible ? 'text-indigo-700' : 'text-slate-400'}
                                >
                                  {sharingId === a.assignment_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />}
                                </Button>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            }
          </TabsContent>

          <TabsContent value="catalog">
            {loading ? <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></div> :
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap" data-testid="framework-filter">
                  <span className="text-xs text-slate-500 flex items-center gap-1"><Filter className="h-3.5 w-3.5" /> Framework:</span>
                  {[
                    { v: 'all', label: `Todos (${courses.length})` },
                    { v: 'nis2', label: `NIS2 (${(coursesByFramework.nis2 || 0) + (coursesByFramework.both || 0)})` },
                    { v: 'ai_act', label: `AI Act (${(coursesByFramework.ai_act || 0) + (coursesByFramework.both || 0)})` },
                    { v: 'both', label: `Ambos (${coursesByFramework.both || 0})` },
                  ].map(o => (
                    <Button
                      key={o.v}
                      size="sm"
                      variant={frameworkFilter === o.v ? 'default' : 'outline'}
                      onClick={() => setFrameworkFilter(o.v)}
                      data-testid={`framework-filter-${o.v}`}
                    >
                      {o.label}
                    </Button>
                  ))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredCourses.map(c => {
                    const cat = CATEGORY_META[c.category] || { label: c.category, tone: 'bg-slate-50 text-slate-700 border-slate-200' };
                    const fw = FRAMEWORK_META[c.framework] || FRAMEWORK_META.both;
                    return (
                      <Card key={c.course_id} data-testid={`course-${c.course_id}`}>
                        <CardContent className="p-5">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="secondary" className={fw.tone} data-testid={`course-framework-${c.course_id}`}>{fw.label}</Badge>
                            <Badge variant="secondary" className={cat.tone}>{cat.label}</Badge>
                            {c.mandatory && <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200 text-[10px]">Obrigatório</Badge>}
                          </div>
                          <h3 className="font-semibold text-slate-900 mb-1">{c.title}</h3>
                          <p className="text-sm text-slate-600 line-clamp-3">{c.description}</p>
                          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                            <span>⏱ {c.duration_min} min</span>
                            <span>📝 {(c.quiz || []).length} perguntas</span>
                            <span>🎯 {c.pass_threshold}% para aprovar</span>
                          </div>
                          {isAdmin && (
                            <Button className="w-full mt-3" variant="outline" size="sm" onClick={() => openAssign(c)} data-testid={`assign-${c.course_id}`}>
                              <Send className="h-3.5 w-3.5 mr-1" /> Atribuir
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                  {filteredCourses.length === 0 && (
                    <div className="col-span-full p-8 text-center text-slate-400 text-sm">
                      Nenhum curso para o filtro selecionado.
                    </div>
                  )}
                </div>
              </div>
            }
          </TabsContent>

          {isBoardView && (
            <TabsContent value="board">
              <div className="space-y-4">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <LayoutDashboard className="h-5 w-5 text-indigo-600" /> Painel Executivo · Literacia & Formação
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Cobertura de formação lado-a-lado — <strong>NIS2 Art. 21(2)(g)</strong> vs <strong>EU AI Act Art. 4</strong>. Evidência formal para o Board Pack anual.
                    </p>
                  </div>
                  {boardStats?.generated_at && (
                    <div className="text-[11px] text-slate-400 font-mono">
                      Gerado em {boardStats.generated_at.slice(0, 19).replace('T', ' ')}
                    </div>
                  )}
                </div>

                {!boardStats ? (
                  <Card><CardContent className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-indigo-600" /></CardContent></Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'nis2', title: 'NIS2 · Cibersegurança', tone: 'from-sky-500 to-sky-700', ring: 'ring-sky-100', stats: boardStats.frameworks?.nis2 },
                      { key: 'ai_act', title: 'EU AI Act · Literacia IA', tone: 'from-violet-500 to-violet-700', ring: 'ring-violet-100', stats: boardStats.frameworks?.ai_act },
                    ].map(fw => {
                      const s = fw.stats || {};
                      const cov = s.coverage_pct ?? 0;
                      return (
                        <Card key={fw.key} className={`ring-1 ${fw.ring}`} data-testid={`board-card-${fw.key}`}>
                          <CardContent className="p-5">
                            <div className={`inline-flex items-center gap-2 rounded-md px-2.5 py-1 text-white text-xs font-semibold bg-gradient-to-r ${fw.tone} mb-3`}>
                              {fw.title}
                            </div>
                            <div className="text-xs text-slate-500 mb-1">{s.regulation}</div>
                            <div className="flex items-end justify-between mb-3">
                              <div>
                                <div className="text-4xl font-bold tabular-nums text-slate-900" data-testid={`board-${fw.key}-coverage`}>{cov}%</div>
                                <div className="text-xs text-slate-500 mt-0.5">Cobertura de conclusão</div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-semibold tabular-nums text-slate-800">{s.unique_users ?? 0}</div>
                                <div className="text-xs text-slate-500">colaboradores</div>
                              </div>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2 mb-4 overflow-hidden">
                              <div
                                className={`h-full ${cov >= 80 ? 'bg-emerald-500' : cov >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(100, cov)}%` }}
                              />
                            </div>
                            <div className="grid grid-cols-4 gap-2 text-center">
                              <div>
                                <div className="text-sm font-semibold tabular-nums text-slate-900">{s.courses ?? 0}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-wide">Cursos</div>
                              </div>
                              <div>
                                <div className="text-sm font-semibold tabular-nums text-emerald-700">{s.completed ?? 0}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-wide">Concluídas</div>
                              </div>
                              <div>
                                <div className="text-sm font-semibold tabular-nums text-amber-700">{s.in_progress ?? 0}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-wide">Em curso</div>
                              </div>
                              <div>
                                <div className="text-sm font-semibold tabular-nums text-red-700">{s.overdue ?? 0}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-wide">Em atraso</div>
                              </div>
                            </div>
                            <div className="mt-3 text-xs text-slate-500 flex items-center justify-between">
                              <span>Taxa de aprovação nos quizzes</span>
                              <span className="font-semibold text-slate-800 tabular-nums">{s.pass_rate_pct ?? 0}%</span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                {/* Role coverage */}
                {boardStats?.role_coverage?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <Users className="h-4 w-4 text-indigo-600" /> Cobertura por papel
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                          <tr>
                            <th className="text-left px-4 py-3">Role</th>
                            <th className="text-right px-4 py-3">Total</th>
                            <th className="text-right px-4 py-3">Concluídas</th>
                            <th className="text-right px-4 py-3">Cobertura</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {boardStats.role_coverage.map((r, i) => (
                            <tr key={i} data-testid={`board-role-${i}`}>
                              <td className="px-4 py-3 font-medium">{r.role || '—'}</td>
                              <td className="px-4 py-3 text-right tabular-nums">{r.total}</td>
                              <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{r.completed}</td>
                              <td className="px-4 py-3 text-right">
                                <span className={`font-bold ${r.coverage_pct >= 80 ? 'text-emerald-600' : r.coverage_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                                  {r.coverage_pct}%
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                )}

                {/* Latest certificates */}
                {boardStats?.latest_certificates?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <BadgeCheck className="h-4 w-4 text-emerald-600" /> Últimos certificados emitidos
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="divide-y divide-slate-100">
                        {boardStats.latest_certificates.map(c => (
                          <div key={c.assignment_id} className="p-4 hover:bg-slate-50 flex items-center gap-4" data-testid={`board-cert-${c.assignment_id}`}>
                            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
                              <BadgeCheck className="h-5 w-5 text-emerald-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-slate-900 text-sm">{c.user_name || c.user_email}</div>
                              <div className="text-xs text-slate-500 truncate">{c.course_title}</div>
                              <code className="text-[10px] text-slate-400 font-mono">SHA-256: {(c.certificate_hash || '').slice(0, 24)}…</code>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-sm font-bold text-emerald-700 tabular-nums">{c.quiz_score}%</div>
                              <div className="text-[10px] text-slate-400">{(c.completed_at || '').slice(0, 10)}</div>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => downloadCertificate(c.assignment_id)} data-testid={`board-cert-download-${c.assignment_id}`}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                            <a
                              href={`/verify/certificate/${c.certificate_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-indigo-600"
                              title="Verificação pública"
                              data-testid={`board-cert-verify-${c.assignment_id}`}
                            >
                              <BadgeCheck className="h-3.5 w-3.5" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="manage">
              <Card>
                <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Settings2 className="h-4 w-4 text-indigo-600" /> Gestão de cursos (NIS2 · AI Act)
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={triggerAutoAssign} disabled={autoAssigning} data-testid="auto-assign-btn" title="Atribui automaticamente todos os cursos obrigatórios aos utilizadores com o role alvo">
                      {autoAssigning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                      Auto-atribuir obrigatórios
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const { data } = await api.post('/training/recurrences/check');
                          toast.success(`${data.created} reatribuições recorrentes criadas.`);
                          setRefresh(r => r + 1);
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || 'Falha.');
                        }
                      }}
                      data-testid="recurrence-check-btn"
                      title="Verifica cursos com recorrência e reatribui a quem completou há mais de N meses"
                    >
                      <Repeat className="h-3.5 w-3.5 mr-1.5" /> Verificar recorrências
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openCsvDialog}
                      data-testid="csv-import-btn"
                      title="Importar cursos em massa via CSV"
                    >
                      <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Importar CSV
                    </Button>
                    <Button size="sm" onClick={openCreateCourse} data-testid="course-new-btn">
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Novo curso
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="px-6 py-3 border-b border-slate-100 bg-slate-50 text-xs flex flex-wrap gap-4">
                    <div><span className="text-slate-500 uppercase tracking-wide">Total:</span> <strong className="ml-1 tabular-nums">{courses.length}</strong></div>
                    <div><span className="text-slate-500 uppercase tracking-wide">NIS2 apenas:</span> <strong className="ml-1 tabular-nums text-sky-700">{coursesByFramework.nis2 || 0}</strong></div>
                    <div><span className="text-slate-500 uppercase tracking-wide">AI Act apenas:</span> <strong className="ml-1 tabular-nums text-violet-700">{coursesByFramework.ai_act || 0}</strong></div>
                    <div><span className="text-slate-500 uppercase tracking-wide">Ambos:</span> <strong className="ml-1 tabular-nums text-slate-700">{coursesByFramework.both || 0}</strong></div>
                  </div>
                  {courses.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm">Sem cursos. Clique em &quot;Novo curso&quot; para começar.</div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-4 py-3">Curso</th>
                          <th className="text-left px-4 py-3">Framework</th>
                          <th className="text-left px-4 py-3">Categoria</th>
                          <th className="text-right px-4 py-3">Duração</th>
                          <th className="text-right px-4 py-3">Perguntas</th>
                          <th className="text-right px-4 py-3">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {courses.map(c => {
                          const fw = FRAMEWORK_META[c.framework] || FRAMEWORK_META.both;
                          const cat = CATEGORY_META[c.category] || { label: c.category, tone: 'bg-slate-50 text-slate-700 border-slate-200' };
                          return (
                            <tr key={c.course_id} className="hover:bg-slate-50" data-testid={`manage-row-${c.course_id}`}>
                              <td className="px-4 py-3">
                                <div className="font-medium text-slate-900 flex items-center gap-1.5">
                                  {c.title}
                                  {c.recurrence_months > 0 && (
                                    <Badge variant="secondary" className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200" title={`Reatribui a cada ${c.recurrence_months} meses`}>
                                      <Repeat className="h-2.5 w-2.5 mr-0.5" /> {c.recurrence_months}m
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-slate-500 font-mono">{c.course_id}{c.mandatory ? ' · Obrigatório' : ''}</div>
                              </td>
                              <td className="px-4 py-3"><Badge variant="secondary" className={fw.tone}>{fw.label}</Badge></td>
                              <td className="px-4 py-3"><Badge variant="secondary" className={cat.tone}>{cat.label}</Badge></td>
                              <td className="px-4 py-3 text-right tabular-nums">{c.duration_min} min</td>
                              <td className="px-4 py-3 text-right tabular-nums">{(c.quiz || []).length}</td>
                              <td className="px-4 py-3 text-right space-x-1">
                                <Button variant="ghost" size="sm" onClick={() => openEditCourse(c)} data-testid={`course-edit-${c.course_id}`}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteCourse(c)} data-testid={`course-delete-${c.course_id}`}>
                                  <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="assignments">
              <Card>
                <CardContent className="p-0">
                  {allAssignments.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Sem atribuições ainda.</div> :
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-4 py-3">Utilizador</th>
                          <th className="text-left px-4 py-3">Curso</th>
                          <th className="text-left px-4 py-3">Estado</th>
                          <th className="text-right px-4 py-3">Score</th>
                          <th className="text-left px-4 py-3">Atribuído</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {allAssignments.map(a => {
                          const s = STATUS_META[a.status] || STATUS_META.assigned;
                          return (
                            <tr key={a.assignment_id} className="hover:bg-slate-50" data-testid={`assignment-${a.assignment_id}`}>
                              <td className="px-4 py-3">
                                <div className="font-medium">{a.user_name}</div>
                                <div className="text-xs text-slate-500">{a.user_email} · {a.user_role}</div>
                              </td>
                              <td className="px-4 py-3">{a.course_title}</td>
                              <td className="px-4 py-3"><Badge variant="secondary" className={s.tone}>{s.label}</Badge></td>
                              <td className="px-4 py-3 text-right tabular-nums">{a.quiz_score !== null && a.quiz_score !== undefined ? `${a.quiz_score}%` : '—'}</td>
                              <td className="px-4 py-3 text-xs text-slate-500">{(a.assigned_at || '').slice(0, 10)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  }
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="literacy">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Award className="h-4 w-4 text-indigo-600" /> Cobertura de literacia por role/departamento
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {literacyRows.length === 0 ? <div className="p-8 text-center text-slate-400 text-sm">Sem dados suficientes ainda.</div> :
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                        <tr>
                          <th className="text-left px-4 py-3">Role</th>
                          <th className="text-left px-4 py-3">Departamento</th>
                          <th className="text-right px-4 py-3">Total</th>
                          <th className="text-right px-4 py-3">Concluídos</th>
                          <th className="text-right px-4 py-3">Cobertura</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {literacyRows.map((r, i) => (
                          <tr key={i} data-testid={`literacy-row-${i}`}>
                            <td className="px-4 py-3 font-medium">{r.role || '—'}</td>
                            <td className="px-4 py-3 text-slate-600">{r.department || '—'}</td>
                            <td className="px-4 py-3 text-right tabular-nums">{r.total}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{r.completed}</td>
                            <td className="px-4 py-3 text-right">
                              <span className={`font-bold ${r.coverage_pct >= 80 ? 'text-emerald-600' : r.coverage_pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                                {r.coverage_pct}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  }
                </CardContent>
              </Card>
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="phishing">
              <Card>
                <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Fish className="h-4 w-4 text-red-600" /> Campanhas de phishing (evidência NIS2-TRN-002)
                  </CardTitle>
                  <Button size="sm" onClick={() => setPhishDialog(true)} data-testid="phishing-add-btn">
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Registar campanha
                  </Button>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="px-6 py-3 border-b border-slate-100 bg-slate-50">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div><span className="text-slate-500 uppercase tracking-wide">Total:</span> <strong className="ml-1 tabular-nums">{phishStats?.total ?? 0}</strong></div>
                      <div><span className="text-slate-500 uppercase tracking-wide">Últimos 12m:</span> <strong className="ml-1 tabular-nums">{phishStats?.last_12_months ?? 0}</strong></div>
                      <div><span className="text-slate-500 uppercase tracking-wide">Taxa média cliques:</span> <strong className="ml-1 tabular-nums text-red-600">{phishStats?.avg_click_rate_pct ?? '—'}%</strong></div>
                      <div><span className="text-slate-500 uppercase tracking-wide">Taxa média reporte:</span> <strong className="ml-1 tabular-nums text-emerald-600">{phishStats?.avg_report_rate_pct ?? '—'}%</strong></div>
                    </div>
                  </div>
                  {phishing.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm">
                      Sem campanhas registadas. Regista aqui os resultados de simulações executadas em <strong>CanIPhish, KnowBe4</strong> ou outras plataformas — este módulo NÃO envia phishing, apenas guarda a evidência.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {phishing.map((p) => (
                        <div key={p.campaign_id} className="p-4 hover:bg-slate-50" data-testid={`phishing-row-${p.campaign_id}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span className="font-semibold text-slate-900">{p.name}</span>
                                <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-700 border-slate-200 uppercase">{p.provider}</Badge>
                                <span className="text-xs text-slate-500">{p.date}</span>
                                {p.remedial_training_assigned && <Badge variant="secondary" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Formação remediativa atribuída</Badge>}
                              </div>
                              <div className="flex flex-wrap gap-4 text-xs mt-2">
                                <span><span className="text-slate-500">Alvos:</span> <strong className="tabular-nums">{p.target_count}</strong></span>
                                {p.clicked_count != null && <span><span className="text-slate-500">Cliques:</span> <strong className="tabular-nums text-red-600">{p.clicked_count}</strong> ({p.click_rate_pct}%)</span>}
                                {p.reported_count != null && <span><span className="text-slate-500">Reportados:</span> <strong className="tabular-nums text-emerald-600">{p.reported_count}</strong> ({p.report_rate_pct}%)</span>}
                                {p.template_type && <span><span className="text-slate-500">Template:</span> <em>{p.template_type}</em></span>}
                              </div>
                              {p.notes && <p className="text-xs text-slate-500 mt-2">{p.notes}</p>}
                              {p.report_url && <a href={p.report_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline mt-1 inline-block">Ver relatório</a>}
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => deletePhishing(p.campaign_id)} data-testid={`phishing-delete-${p.campaign_id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-red-600" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Course viewer + quiz */}
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) { setViewing(null); setResult(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewing?.course.title}</DialogTitle>
            <DialogDescription>{viewing?.course.description}</DialogDescription>
          </DialogHeader>

          {result ? (
            <div className={`text-center p-8 rounded-lg ${result.passed ? 'bg-emerald-50' : 'bg-red-50'}`} data-testid="quiz-result">
              {result.passed ? <CheckCircle2 className="h-16 w-16 text-emerald-600 mx-auto mb-3" /> : <XCircle className="h-16 w-16 text-red-600 mx-auto mb-3" />}
              <div className="text-4xl font-bold tabular-nums mb-1">{result.score}%</div>
              <div className={`text-sm ${result.passed ? 'text-emerald-700' : 'text-red-700'}`}>
                {result.passed ? 'Aprovado — certificado emitido' : `Reprovado — mínimo ${result.threshold}%. Reveja o material e volte a tentar.`}
              </div>
            </div>
          ) : (
            <>
              <div className="prose prose-sm max-w-none text-slate-700 bg-slate-50 rounded p-4 max-h-64 overflow-y-auto whitespace-pre-wrap">
                {viewing?.course.body_md}
              </div>

              {(viewing?.course.quiz || []).length > 0 && (
                <div className="space-y-4 mt-4">
                  <h3 className="font-semibold text-slate-900">Quiz de avaliação</h3>
                  {viewing.course.quiz.map((q, i) => (
                    <div key={i} className="border border-slate-200 rounded p-3" data-testid={`quiz-q-${i}`}>
                      <div className="font-medium text-sm text-slate-900 mb-2">{i + 1}. {q.q}</div>
                      <div className="space-y-1">
                        {q.options.map((opt, j) => (
                          <label key={j} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                            <input
                              type="radio"
                              name={`q-${i}`}
                              checked={answers[i] === j}
                              onChange={() => {
                                const next = [...answers];
                                next[i] = j;
                                setAnswers(next);
                              }}
                              data-testid={`quiz-q-${i}-opt-${j}`}
                            />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setViewing(null); setResult(null); }}>Fechar</Button>
            {!result && (viewing?.course.quiz || []).length > 0 && viewing?.assignment.status !== 'completed' && (
              <Button onClick={submitQuiz} disabled={submitting} data-testid="quiz-submit-btn">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A submeter…</> : 'Submeter respostas'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={!!assignCourse} onOpenChange={(o) => { if (!o) setAssignCourse(null); }}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Atribuir: {assignCourse?.title}</DialogTitle>
            <DialogDescription>Introduza emails dos utilizadores (um por linha ou separados por vírgula).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Emails dos destinatários</Label>
              <Textarea rows={5} value={assignEmails} onChange={e => setAssignEmails(e.target.value)} placeholder="user1@empresa.pt&#10;user2@empresa.pt" data-testid="assign-emails" />
            </div>
            <div>
              <Label>Data limite (opcional)</Label>
              <Input type="date" value={assignDue} onChange={e => setAssignDue(e.target.value)} data-testid="assign-due" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignCourse(null)}>Cancelar</Button>
            <Button onClick={submitAssign} disabled={assigning} data-testid="assign-submit">
              {assigning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A atribuir…</> : <><Send className="h-4 w-4 mr-2" />Atribuir</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phishing evidence dialog */}
      <Dialog open={phishDialog} onOpenChange={setPhishDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Fish className="h-5 w-5 text-red-600" /> Registar campanha de phishing (evidência)</DialogTitle>
            <DialogDescription>
              Guarde os resultados de uma simulação já executada em <strong>CanIPhish / KnowBe4 / etc</strong>. NIS2-TRN-002 exige evidência de simulações periódicas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Provider</Label>
                <Select value={phishForm.provider} onValueChange={v => setPhishForm({ ...phishForm, provider: v })}>
                  <SelectTrigger data-testid="phish-provider"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="caniphish">CanIPhish</SelectItem>
                    <SelectItem value="knowbe4">KnowBe4</SelectItem>
                    <SelectItem value="phishme">PhishMe / Cofense</SelectItem>
                    <SelectItem value="gophish">GoPhish</SelectItem>
                    <SelectItem value="manual">Manual / Interno</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Data *</Label>
                <Input type="date" value={phishForm.date} onChange={e => setPhishForm({ ...phishForm, date: e.target.value })} data-testid="phish-date" />
              </div>
            </div>
            <div>
              <Label>Nome da campanha *</Label>
              <Input value={phishForm.name} onChange={e => setPhishForm({ ...phishForm, name: e.target.value })} placeholder="Ex: Simulação Q3 2026 — Fatura urgente" data-testid="phish-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Nº alvos *</Label>
                <Input type="number" min={1} value={phishForm.target_count} onChange={e => setPhishForm({ ...phishForm, target_count: e.target.value })} data-testid="phish-targets" />
              </div>
              <div>
                <Label>Cliques</Label>
                <Input type="number" min={0} value={phishForm.clicked_count} onChange={e => setPhishForm({ ...phishForm, clicked_count: e.target.value })} data-testid="phish-clicks" />
              </div>
              <div>
                <Label>Reportados</Label>
                <Input type="number" min={0} value={phishForm.reported_count} onChange={e => setPhishForm({ ...phishForm, reported_count: e.target.value })} data-testid="phish-reported" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Template</Label>
                <Input value={phishForm.template_type} onChange={e => setPhishForm({ ...phishForm, template_type: e.target.value })} placeholder="Ex: invoice_urgent, ceo_wire" />
              </div>
              <div>
                <Label>URL do relatório</Label>
                <Input value={phishForm.report_url} onChange={e => setPhishForm({ ...phishForm, report_url: e.target.value })} placeholder="https://caniphish.com/…" />
              </div>
            </div>
            <div>
              <Label>Notas</Label>
              <Textarea rows={2} value={phishForm.notes} onChange={e => setPhishForm({ ...phishForm, notes: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={phishForm.remedial_training_assigned} onChange={e => setPhishForm({ ...phishForm, remedial_training_assigned: e.target.checked })} data-testid="phish-remedial" />
              <span>Formação remediativa atribuída aos utilizadores que clicaram</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPhishDialog(false)}>Cancelar</Button>
            <Button onClick={submitPhishing} data-testid="phish-submit">Registar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Course editor dialog (admin) */}
      <Dialog open={!!courseDialog} onOpenChange={(o) => { if (!o) setCourseDialog(false); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="course-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-indigo-600" />
              {courseDialog?.mode === 'edit' ? `Editar curso: ${courseDialog.data?.title}` : 'Novo curso'}
            </DialogTitle>
            <DialogDescription>
              Classifica cada curso por <strong>framework</strong> (NIS2, AI Act ou ambos) para atribuir formação corretamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Título *</Label>
                <Input value={courseForm.title} onChange={e => setCourseForm({ ...courseForm, title: e.target.value })} placeholder="Ex: Literacia em IA — Fundamentos" data-testid="course-title" />
              </div>
              <div className="col-span-2">
                <Label>Descrição *</Label>
                <Textarea rows={2} value={courseForm.description} onChange={e => setCourseForm({ ...courseForm, description: e.target.value })} data-testid="course-description" />
              </div>
              <div>
                <Label>Framework *</Label>
                <Select value={courseForm.framework} onValueChange={v => setCourseForm({ ...courseForm, framework: v })}>
                  <SelectTrigger data-testid="course-framework"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nis2">NIS2</SelectItem>
                    <SelectItem value="ai_act">AI Act</SelectItem>
                    <SelectItem value="both">Ambos (NIS2 + AI Act)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoria *</Label>
                <Select value={courseForm.category} onValueChange={v => setCourseForm({ ...courseForm, category: v })}>
                  <SelectTrigger data-testid="course-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ai_literacy">Literacia IA</SelectItem>
                    <SelectItem value="cybersecurity">Cibersegurança</SelectItem>
                    <SelectItem value="phishing">Phishing</SelectItem>
                    <SelectItem value="gdpr">GDPR</SelectItem>
                    <SelectItem value="incident_response">Resposta Incidentes</SelectItem>
                    <SelectItem value="governance">Governança</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duração (min) *</Label>
                <Input type="number" min={5} max={600} value={courseForm.duration_min} onChange={e => setCourseForm({ ...courseForm, duration_min: e.target.value })} data-testid="course-duration" />
              </div>
              <div>
                <Label>Nota mínima (%)</Label>
                <Input type="number" min={0} max={100} value={courseForm.pass_threshold} onChange={e => setCourseForm({ ...courseForm, pass_threshold: e.target.value })} data-testid="course-threshold" />
              </div>
              <div className="col-span-2">
                <Label>Roles alvo (separados por vírgula)</Label>
                <Input value={courseForm.target_roles} onChange={e => setCourseForm({ ...courseForm, target_roles: e.target.value })} placeholder="colaborador, diretor, ciso, board_member" data-testid="course-roles" />
              </div>
              <label className="flex items-center gap-2 text-sm col-span-2">
                <input type="checkbox" checked={courseForm.mandatory} onChange={e => setCourseForm({ ...courseForm, mandatory: e.target.checked })} data-testid="course-mandatory" />
                <span>Obrigatório para todos os colaboradores do(s) role(s) alvo</span>
              </label>
              <div className="col-span-2 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                <Repeat className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                <div className="flex-1">
                  <Label className="text-xs">Formação recorrente (meses)</Label>
                  <div className="text-[11px] text-slate-500">Após conclusão, o curso é reatribuído automaticamente ao mesmo colaborador ao fim de N meses. 0 = não recorrente.</div>
                </div>
                <Input
                  type="number"
                  min={0}
                  max={60}
                  value={courseForm.recurrence_months ?? 0}
                  onChange={e => setCourseForm({ ...courseForm, recurrence_months: e.target.value })}
                  className="w-20"
                  data-testid="course-recurrence"
                />
              </div>
              <div className="col-span-2">
                <Label>Conteúdo (Markdown) *</Label>
                <Textarea rows={8} value={courseForm.body_md} onChange={e => setCourseForm({ ...courseForm, body_md: e.target.value })} placeholder="# Título&#10;&#10;## Secção 1..." data-testid="course-body" />
              </div>
            </div>

            {/* Quiz builder */}
            <div className="border border-slate-200 rounded p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">Quiz de avaliação</div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={quizPreview ? 'default' : 'outline'}
                    onClick={() => { setQuizPreview(!quizPreview); setPreviewAnswers(new Array((courseForm.quiz || []).length).fill(-1)); }}
                    data-testid="quiz-preview-toggle"
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" /> {quizPreview ? 'Editar' : 'Pré-visualizar'}
                  </Button>
                  {!quizPreview && (
                    <Button size="sm" variant="outline" onClick={addQuizQ} data-testid="quiz-add-q">
                      <Plus className="h-3.5 w-3.5 mr-1" /> Nova pergunta
                    </Button>
                  )}
                </div>
              </div>
              {(courseForm.quiz || []).length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-4">Sem perguntas. Adicione pelo menos uma para permitir avaliação.</div>
              ) : quizPreview ? (
                <div className="space-y-3 bg-white p-3 rounded border border-indigo-100" data-testid="quiz-preview-panel">
                  <div className="text-[11px] text-indigo-600 font-semibold uppercase tracking-wider flex items-center gap-1">
                    <Eye className="h-3 w-3" /> Pré-visualização — assim é que o formando vê o quiz
                  </div>
                  {(courseForm.quiz || []).map((q, i) => (
                    <div key={i} className="border border-slate-200 rounded p-3" data-testid={`quiz-preview-q-${i}`}>
                      <div className="font-medium text-sm text-slate-900 mb-2">{i + 1}. {q.q || <em className="text-slate-400">(pergunta sem enunciado)</em>}</div>
                      <div className="space-y-1">
                        {(q.options || []).map((opt, j) => (
                          <label key={j} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                            <input
                              type="radio"
                              name={`preview-q-${i}`}
                              checked={previewAnswers[i] === j}
                              onChange={() => {
                                const next = [...previewAnswers];
                                next[i] = j;
                                setPreviewAnswers(next);
                              }}
                              data-testid={`quiz-preview-q-${i}-opt-${j}`}
                            />
                            <span>{opt || <em className="text-slate-400">(opção vazia)</em>}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2">
                    Nota mínima: <strong>{courseForm.pass_threshold}%</strong> · {(courseForm.quiz || []).length} perguntas
                  </div>
                </div>
              ) : (
                (courseForm.quiz || []).map((q, i) => (
                  <div key={i} className="border border-slate-100 rounded p-3 space-y-2 bg-slate-50" data-testid={`course-quiz-q-${i}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">Pergunta {i + 1}</Label>
                        <Input value={q.q} onChange={e => updateQuizQ(i, { q: e.target.value })} data-testid={`course-quiz-q-text-${i}`} />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeQuizQ(i)} data-testid={`course-quiz-q-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-600" />
                      </Button>
                    </div>
                    <div className="space-y-1">
                      {(q.options || []).map((opt, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`course-quiz-correct-${i}`}
                            checked={q.correct_index === j}
                            onChange={() => updateQuizQ(i, { correct_index: j })}
                            data-testid={`course-quiz-q-${i}-correct-${j}`}
                          />
                          <Input
                            value={opt}
                            onChange={e => updateQuizOption(i, j, e.target.value)}
                            placeholder={`Opção ${j + 1}`}
                            data-testid={`course-quiz-q-${i}-opt-${j}`}
                          />
                        </div>
                      ))}
                      <Button size="sm" variant="ghost" onClick={() => addQuizOption(i)} data-testid={`course-quiz-q-${i}-add-opt`}>
                        <Plus className="h-3 w-3 mr-1" /> Adicionar opção
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCourseDialog(false)}>Cancelar</Button>
            <Button onClick={submitCourse} disabled={savingCourse} data-testid="course-save-btn">
              {savingCourse ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A guardar…</> : (courseDialog?.mode === 'edit' ? 'Guardar alterações' : 'Criar curso')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV import dialog */}
      <Dialog open={csvDialog} onOpenChange={setCsvDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="csv-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-indigo-600" />
              Importar cursos via CSV
            </DialogTitle>
            <DialogDescription>
              Colunas obrigatórias: <code className="text-[11px]">title, description, category, duration_min, body_md</code>.
              Opcionais: <code className="text-[11px]">framework, mandatory, target_roles, pass_threshold, recurrence_months</code>.
              Cursos com título duplicado são ignorados (idempotente).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={downloadCsvTemplate} data-testid="csv-download-template">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Descarregar template
              </Button>
              <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 text-sm cursor-pointer hover:bg-slate-50" data-testid="csv-file-picker-label">
                <Upload className="h-3.5 w-3.5" /> Escolher ficheiro CSV
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  data-testid="csv-file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => setCsvContent(ev.target.result);
                    reader.readAsText(file);
                  }}
                />
              </label>
              <span className="text-xs text-slate-500">
                Ou cole o CSV diretamente abaixo:
              </span>
            </div>
            <Textarea
              rows={10}
              value={csvContent}
              onChange={(e) => setCsvContent(e.target.value)}
              placeholder="title,description,category,framework,duration_min,mandatory,target_roles,body_md,pass_threshold,recurrence_months&#10;Curso 1,Descrição...,cybersecurity,nis2,30,true,colaborador,Conteúdo Markdown...,70,12"
              className="font-mono text-xs"
              data-testid="csv-textarea"
            />

            {csvResult && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm space-y-2" data-testid="csv-result">
                <div className="flex items-center gap-4 flex-wrap">
                  <span><strong className="text-emerald-700 tabular-nums">{csvResult.created}</strong> criados</span>
                  <span><strong className="text-amber-700 tabular-nums">{csvResult.skipped_duplicate}</strong> duplicados (ignorados)</span>
                  <span><strong className="text-red-700 tabular-nums">{csvResult.errors?.length || 0}</strong> erros</span>
                </div>
                {csvResult.errors?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                    {csvResult.errors.map((e, i) => (
                      <div key={i} className="text-red-700" data-testid={`csv-error-${i}`}>
                        Linha {e.row}: {e.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCsvDialog(false)}>Fechar</Button>
            <Button onClick={submitCsvImport} disabled={csvImporting || !csvContent} data-testid="csv-submit-btn">
              {csvImporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A importar…</> : <>Importar cursos</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


    </Layout>
  );
}
